import CORE_FIELDS from 'v1/helpers/consts/CORE_FIELDS';
import * as entityTypes from 'v1/helpers/entityTypes';
import type { ReportEntityType } from 'v5/api/report-templates/reportTemplates.api';

type TypedEntity = 'contact' | 'production' | 'booking';

export interface ReportFieldNode {
  key: string;
  name: string;
  children?: ReportFieldNode[];
}

export interface ReportFieldOption {
  key: string;
  /** The field's own name, which a template stores as the column's name (as v1 did). */
  name: string;
  /** The name with its parent path, as the picker shows it. */
  label: string;
}

export interface ReportEntityTypeInput {
  id: number;
  archived: boolean;
  metastructure?: {
    fields: {
      active: boolean;
      type: string;
      custom_field_definition_id?: number | null;
      custom_fieldgroup_definition_id?: number | null;
    }[];
  } | null;
}

export interface ReportCustomFieldDefinitionInput {
  id: number;
  name: string;
  archived?: boolean;
}

export interface ReportCustomFieldGroupDefinitionInput {
  id: number;
  name: string;
  archived?: boolean;
  custom_field_definitions: { id: number; name: string }[];
}

export interface ReportFieldTreeInput {
  entityType: ReportEntityType;
  /** Narrows the root entity's custom fields to one type; nested entities always use all their types. */
  selectedTypeId: number | null;
  types: Record<TypedEntity, ReportEntityTypeInput[]>;
  customFieldDefinitions: ReportCustomFieldDefinitionInput[];
  customFieldGroupDefinitions: ReportCustomFieldGroupDefinitionInput[];
  allowCheckins: boolean;
}

const CHECKIN_FIELDS: ReportFieldNode = {
  key: 'checkins',
  name: 'Checkins',
  children: [
    { key: 'checkins.booking_date', name: 'Booking Date' },
    { key: 'checkins.checked_in_timestamp', name: 'Checked In' },
    { key: 'checkins.checked_out_timestamp', name: 'Checked Out' },
    { key: 'checkins.status', name: 'Status' },
    { key: 'checkins.badge_number', name: 'Badge Number' },
    { key: 'checkins.user_id', name: 'Checked In By' }
  ]
};

function getCoreFields(entityType: ReportEntityType): ReportFieldNode[] {
  switch (entityType) {
    case 'contact':
      return CORE_FIELDS[entityTypes.CONTACT];
    case 'callout':
      return CORE_FIELDS[entityTypes.CALLOUT];
    case 'shortlist':
      return CORE_FIELDS[entityTypes.SHORTLIST];
    case 'callsheet':
      return CORE_FIELDS[entityTypes.CALLSHEET];
    case 'production':
      return CORE_FIELDS[entityTypes.PRODUCTION];
    case 'booking':
      return CORE_FIELDS[entityTypes.BOOKING];
  }
}

function getTypedEntity(entityType: ReportEntityType): TypedEntity | null {
  switch (entityType) {
    case 'contact':
    case 'production':
    case 'booking':
      return entityType;
    case 'callout':
    case 'shortlist':
    case 'callsheet':
      return null;
  }
}

/** A nested core node holds a related entity when its last key segment names one (expenses have no types, so they add nothing). */
function getNestedEntity(field: ReportFieldNode): TypedEntity | null {
  switch (field.key.split('.').at(-1)) {
    case 'resources':
    case 'resource':
      return 'contact';
    case 'productions':
    case 'production':
      return 'production';
    case 'bookings':
    case 'booking':
      return 'booking';
    default:
      return null;
  }
}

function getCustomFieldNodes(
  entity: TypedEntity,
  selectedTypeId: number | null,
  input: ReportFieldTreeInput,
  pathPrefix: string
): ReportFieldNode[] {
  const fields = new Map<number, ReportCustomFieldDefinitionInput>();
  const groups = new Map<number, ReportCustomFieldGroupDefinitionInput>();
  input.types[entity]
    .filter(
      type =>
        (selectedTypeId === null || type.id === selectedTypeId) &&
        !type.archived
    )
    .flatMap(type => type.metastructure?.fields ?? [])
    .filter(field => field.active)
    .forEach(field => {
      const fieldId = field.custom_field_definition_id;
      const groupId = field.custom_fieldgroup_definition_id;
      if (field.type === 'CUSTOM_FIELD' && fieldId) {
        const definition = input.customFieldDefinitions.find(
          d => d.id === fieldId
        );
        if (definition && !definition.archived) fields.set(fieldId, definition);
      }
      if (field.type === 'CUSTOM_FIELD_GROUP' && groupId) {
        const definition = input.customFieldGroupDefinitions.find(
          d => d.id === groupId
        );
        if (definition && !definition.archived) groups.set(groupId, definition);
      }
    });

  return [
    ...[...fields.values()].map(definition => ({
      key: `${pathPrefix}custom_${definition.id}`,
      name: definition.name
    })),
    ...[...groups.values()].map(group => {
      const groupKey = `${pathPrefix}custom_fieldgroup_${group.id}`;
      return {
        key: groupKey,
        name: group.name,
        children: group.custom_field_definitions.map(child => ({
          key: `${groupKey}.custom_${child.id}`,
          name: child.name
        }))
      };
    })
  ];
}

function withCustomFields(
  fields: ReportFieldNode[],
  entity: TypedEntity | null,
  selectedTypeId: number | null,
  input: ReportFieldTreeInput,
  pathPrefix: string
): ReportFieldNode[] {
  const nested = fields.map(field => {
    if (!field.children) return field;
    // Nested core keys carry the related entity's path, which can differ from the node's own key.
    const firstPathKey = field.children.find(c => c.key.includes('.'))?.key;
    const childPrefix = firstPathKey
      ? `${firstPathKey.split('.').slice(0, -1).join('.')}.`
      : `${field.key}.`;
    return {
      ...field,
      children: withCustomFields(
        field.children,
        getNestedEntity(field),
        null,
        input,
        childPrefix
      )
    };
  });
  const custom = entity
    ? getCustomFieldNodes(entity, selectedTypeId, input, pathPrefix)
    : [];
  return [...nested, ...custom];
}

function sortByName(fields: ReportFieldNode[]): ReportFieldNode[] {
  return [...fields]
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map(field =>
      field.children
        ? { ...field, children: sortByName(field.children) }
        : field
    );
}

/** The exportable fields of an entity as v1's data export builds them; existing report templates store these keys. */
export function buildReportFieldTree(
  input: ReportFieldTreeInput
): ReportFieldNode[] {
  const fields = withCustomFields(
    [...getCoreFields(input.entityType), ...CORE_FIELDS.global],
    getTypedEntity(input.entityType),
    input.selectedTypeId,
    input,
    ''
  );
  const checkins =
    input.allowCheckins && input.entityType === 'booking'
      ? [CHECKIN_FIELDS]
      : [];
  return sortByName([...fields, ...checkins]);
}

/** Selectable leaves in tree order; a node with children only groups them, as in v1's field picker. */
export function listSelectableReportFields(
  tree: ReportFieldNode[]
): ReportFieldOption[] {
  const walk = (
    nodes: ReportFieldNode[],
    parentNames: string[]
  ): ReportFieldOption[] =>
    nodes.flatMap(node => {
      const path = [...parentNames, node.name];
      return node.children
        ? walk(node.children, path)
        : [{ key: node.key, name: node.name, label: path.join(' › ') }];
    });
  return walk(tree, []);
}
